import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import AppRoutes from '@/routes/AppRoutes';
import { Button } from '@/components/ui/button';
import { SilkToaster } from '@workspace/silk-ui';
import { NavigationContextValue, NavigationProvider } from '@/context/NavigationContext';
import { PreferencesProvider, usePreferences } from '@/context/PreferencesContext';
import { SessionProvider, useSession } from '@/context/SessionContext';
import { ScopeProvider, useScope } from '@workspace/scope-context';
import { TourProvider } from '@workspace/tour';
import { isTourDisabled } from '@/lib/tourPreferences';

function useBrowserNavigation(): NavigationContextValue {
  const [path, setPath] = useState(() => window.location.pathname + window.location.search);

  useEffect(() => {
    const handler = () => setPath(window.location.pathname + window.location.search);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  return useMemo(() => ({
    path,
    navigate: (nextPath: string, options?: { replace?: boolean }) => {
      if (!nextPath || nextPath === path) {
        return;
      }
      if (options?.replace) {
        window.history.replaceState({}, '', nextPath);
      } else {
        window.history.pushState({}, '', nextPath);
      }
      setPath(nextPath);
    }
  }), [path]);
}

function SessionGate({ navigation }: { navigation: NavigationContextValue }) {
  const { session, loading, error, refresh, login } = useSession();
  const redirecting = useRef(false);
  const signedOut = useMemo(() => {
    const search = navigation.path.includes('?') ? navigation.path.slice(navigation.path.indexOf('?') + 1) : '';
    return new URLSearchParams(search).get('signed_out') === '1';
  }, [navigation.path]);
  const shouldRedirect = !loading && !session && !signedOut && !redirecting.current;

  useEffect(() => {
    console.log('[SessionGate] Current path:', navigation.path);
    if (navigation.path === '/' || navigation.path === '') {
      console.log('[SessionGate] Redirecting to /admin/users');
      navigation.navigate('/admin/users', { replace: true });
    }
  }, [navigation]);

  useEffect(() => {
    if (!shouldRedirect) {
      return;
    }
    redirecting.current = true;
    login(window.location.href);
  }, [shouldRedirect, login]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
        <p className="text-sm font-medium">Loading dashboard…</p>
      </div>
    );
  }

  if (!session) {
    if (signedOut) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center text-foreground">
          <p className="text-lg font-semibold">You have been signed out.</p>
          <p className="max-w-md text-sm text-muted-foreground">
            Your dashboard session has ended. Sign in again when you are ready.
          </p>
          <Button variant="default" onClick={() => login(`${window.location.origin}/admin/users`)}>
            Sign in again
          </Button>
        </div>
      );
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
        <p className="text-sm font-medium">Redirecting to login…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-foreground">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-6 w-6" aria-hidden />
          <p className="text-sm font-medium">{error}</p>
        </div>
        <Button variant="outline" onClick={() => refresh()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <NavigationProvider value={navigation}>
      <AppRoutes path={navigation.path} />
    </NavigationProvider>
  );
}

function DashboardScopeBootstrap({ children }: { children: ReactNode }) {
  const { loading, allowGlobal, mode, activeTenant, selectGlobal } = useScope();
  const { refresh } = useSession();
  const previousScope = useRef<string>();
  const scopeKey = `${mode}:${activeTenant?.id ?? ''}`;
  useEffect(() => {
    if (loading) return;
    const changed = previousScope.current !== undefined && previousScope.current !== scopeKey;
    previousScope.current = scopeKey;
    if (changed) void refresh();
  }, [loading, scopeKey, refresh]);

  useEffect(() => {
    if (loading || !allowGlobal) {
      return;
    }
    const alreadyGlobal = mode === 'global' && !activeTenant;
    if (alreadyGlobal) {
      return;
    }
    void selectGlobal().catch(() => {
      // Keep retrying on subsequent scope changes to enforce global-only behavior.
    });
  }, [allowGlobal, activeTenant, loading, mode, selectGlobal]);

  return children;
}

function DashboardToaster() {
  const { toastPosition } = usePreferences();
  return <SilkToaster key={toastPosition} position={toastPosition} />;
}

export default function App() {
  const navigation = useBrowserNavigation();

  return (
    <SessionProvider>
      <ScopeProvider autoSelectFirstTenant={false}>
        <PreferencesProvider>
          <TourProvider disabled={isTourDisabled()}>
            <DashboardScopeBootstrap>
              <SessionGate navigation={navigation} />
            </DashboardScopeBootstrap>
            <DashboardToaster />
          </TourProvider>
        </PreferencesProvider>
      </ScopeProvider>
    </SessionProvider>
  );
}
